import { createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';
import { generateReduxKeys } from '../../utils';

const initialState = generateReduxKeys({
    orders: [],
    openOrderId: null,
    orderItems: [],
});

const formatOrderItems = (items) => {
    return _(items)
        .groupBy("product_netsuite_id")
        .map((groupedItems, productId) => {
            const base = groupedItems[0];

            const {
                quantity,
                product_case_quantity,
                price_level,
                uom,
            } = base;

            const effectiveQuantity =
                uom?.toLowerCase() === "case"
                    ? quantity * product_case_quantity
                    : quantity;

            const applicableItems = groupedItems.filter(
                ({ product_minimum_quantity }) =>
                    product_minimum_quantity <= effectiveQuantity
            );

            const matchedItem =
                _.maxBy(
                    applicableItems.filter(
                        ({ product_price_level }) => product_price_level === price_level
                    ),
                    "product_minimum_quantity"
                ) ?? base;

            const tiers = groupedItems.map(
                ({
                    product_price_level,
                    product_tier_price,
                    product_minimum_quantity,
                    product_case_quantity,
                }) => ({
                    price_level: product_price_level,
                    tier_price: product_tier_price,
                    minimum_quantity: product_minimum_quantity,
                    case_quantity: product_case_quantity,
                })
            );

            return {
                ...matchedItem,
                product_netsuite_id: Number(productId),
                tiers,
            };
        })
        .value();
};

const orderSlice = createSlice({
    name: 'order',
    initialState,
    reducers: {
        setOrders: (state, action) => {
            state.orders = action.payload;
        },
        setOrderItems: (state, action) => {
            state.orderItems = formatOrderItems(action.payload);
        },
        appendOrderItems: (state, action) => {
            const prevOrderItems = Array.isArray(state.orderItems) ? state.orderItems : [];

            const newOrders = Array.isArray(action.payload)
                ? action.payload
                : [action.payload];

            const merged = _.cloneDeep(prevOrderItems);

            newOrders.forEach((newOrder) => {
                const existing = _.find(merged, { product_netsuite_id: newOrder.product_netsuite_id });

                if (existing) {
                    existing.quantity = Number(newOrder.quantity) || 1;
                } else {
                    merged.push(newOrder);
                }
            });

            state.orderItems = formatOrderItems(merged);
        },
        removeOrderItemById: (state, action) => {
            const updatedOrders = state.orderItems.filter((order) => order.id !== action.payload);
            state.orderItems = updatedOrders;
        },
        setOpenOrderId: (state, action) => {
            state.openOrderId = action.payload;
        },
    },
});

export const { setOrders, setOrderItems, appendOrderItems, removeOrderItemById, setOpenOrderId } = orderSlice.actions;

export default orderSlice.reducer;
